import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { BehaviourSubjectService } from '../services/behaviour-subject.service';
import { CookieService } from '../services/cookie.service';
import { RootService } from '../services/root.service';
import { BehaviorSubject, Subject, from } from 'rxjs';
import { switchMap, take, filter, finalize } from 'rxjs/operators';
import * as CryptoJS from "crypto-js";
import { formatDate, isPlatformBrowser } from "@angular/common";
import { secureStorage } from "../services/secure-storage.service";
import { TokenService } from "../services/token.service";

@Injectable()
export class MyHttpInterceptor implements HttpInterceptor {
    isLoggedIn: boolean = false;
    private refreshTokenInProgress = false;
    private refreshTokenSubject: Subject<any> = new BehaviorSubject<any>(null);
    private generateTokenInProgress = false;
    private generateTokenSubject: Subject<any> = new BehaviorSubject<any>(null);

    constructor(
        private behaviorService: BehaviourSubjectService,
        public router: Router,
        public rootService: RootService,
        private ssrcookieService: CookieService,
        private localStorage: secureStorage,
        private tokenService: TokenService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip interceptor for token generation and refresh endpoints
        if (request.url.indexOf('generate_token') !== -1 || request.url.indexOf('refresh_token') !== -1) {
            return next.handle(this.injectToken(request));
        }

        const accessToken = this.tokenService.getAccessToken();
        const refreshToken = this.tokenService.getRefreshToken();
        const userCode = this.tokenService.getUserCode();

      
        // Check if localStorage was cleared (no user code but tokens exist in localStorage)
        // This indicates the user cleared their data and should get a fresh free search token
        if ((accessToken || refreshToken) && !userCode) {
            // Clear any existing tokens from localStorage to ensure fresh start
            // this.tokenService.clearAuth();
            return this.handleTokenGeneration(request, next);
        }

        // Case 1: User is authenticated (has both token and user code)
        if (accessToken && userCode) {
            // Check if token is expiring soon (within 30 seconds)
            if (this.tokenService.isTokenExpiringSoon(30)) {
                return this.handleTokenRefresh(request, next);
            }
            
            // Token is valid, proceed normally
            return next.handle(this.injectToken(request));
        }

        // Case 2: User has access token but no user code (free search scenario)
        if (accessToken && !userCode) {
            // Check if token is expiring soon (within 30 seconds)
            if (this.tokenService.isTokenExpiringSoon(30)) {
                return this.handleTokenGeneration(request, next);
            }
            
            // Token is valid, proceed normally
            return next.handle(this.injectToken(request));
        }

        // Case 3: User has refresh token and user code but no access token
        if (refreshToken && userCode && !accessToken) {
            return this.handleTokenRefresh(request, next);
        }

        // Case 4: No authentication at all - generate token for free search
        if (!accessToken && !refreshToken && !userCode) {
            return this.handleTokenGeneration(request, next);
        }

        // Default case: inject whatever tokens we have
        return next.handle(this.injectToken(request));
    }

    private handleTokenGeneration(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.generateTokenInProgress) {
            this.generateTokenInProgress = true;
            this.generateTokenSubject.next(null);
            
            // Check if localStorage was cleared (no user code but tokens exist)
            const userCode = this.tokenService.getUserCode();
            const accessToken = this.tokenService.getAccessToken();
            const refreshToken = this.tokenService.getRefreshToken();
            
            // If localStorage is cleared but tokens exist, always generate free search token
            if ((accessToken || refreshToken) && !userCode) {
                const tokenPromise = this.rootService.generateTokenForFreeSearch();
                
                return from(tokenPromise).pipe(
                    switchMap(() => {
                        this.generateTokenInProgress = false;
                        this.generateTokenSubject.next('completed');
                        return next.handle(this.injectToken(request));
                    }),
                    finalize(() => {
                        this.generateTokenInProgress = false;
                    })
                );
            }
            
            // Use appropriate method based on whether user is logged in
            const tokenPromise = userCode ? this.rootService.getToken() : this.rootService.generateTokenForFreeSearch();
            
            return from(tokenPromise).pipe(
                switchMap(() => {
                    this.generateTokenInProgress = false;
                    this.generateTokenSubject.next('completed');
                    return next.handle(this.injectToken(request));
                }),
                finalize(() => {
                    this.generateTokenInProgress = false;
                })
            );
        }
        
        return this.generateTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(() => {
                return next.handle(this.injectToken(request));
            })
        );
    }

    private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.refreshTokenInProgress) {
            this.refreshTokenInProgress = true;
            this.refreshTokenSubject.next(null);
            
            if (isPlatformBrowser(this.platformId)) {
                if (this.localStorage.getItem('eolrefreshTokenInProgress') !== 'true') {
                    this.localStorage.setItem('eolrefreshTokenInProgress', 'true');
                    
                    return this.rootService.refreshToken2().pipe(
                        switchMap((authResponse) => {
                            if (authResponse) {
                                this.refreshTokenInProgress = false;
                                this.localStorage.setItem('eolrefreshTokenInProgress', 'false');
                            }
                            this.refreshTokenSubject.next(authResponse?.data?.refresh_token);
                            return next.handle(this.injectToken(request));
                        })
                    );
                }
            }
        }
        
        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(() => {
                return next.handle(this.injectToken(request));
            })
        );
    }

    getKey(): string {
        const activationDate = new Date();
        let newDate = formatDate(activationDate, 'yyyy-MM-dd' + 'T' + 'HH:mm:ss', 'en-GB')
        newDate = newDate + "Z"
        var skey = "dwjdwi-jdwijdwiwinwdidwindiwmwcinqppkq="
        var salt = "dwindiwmw==";
        var iterations = 128;
        var bytes = CryptoJS.PBKDF2(skey, salt, { keySize: 48, iterations: iterations });
        var iv = CryptoJS.enc.Hex.parse(bytes.toString().slice(0, 32));
        var key = CryptoJS.enc.Hex.parse(bytes.toString().slice(32, 96));
        var encrypteDate = CryptoJS.AES.encrypt(newDate, key, { iv: iv }).toString();
        return encrypteDate
    }

    injectToken(request: HttpRequest<any>) {
        const token = this.tokenService.getAccessToken();
        const userCode = this.tokenService.getUserCode();
        const encryptedDate = this.getKey();

        // For token generation requests, use encrypted date as user code
        if (request.url.indexOf('generate_token') !== -1) {
            const modifiedRequest = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${token || ''}`,
                    'user-code': encryptedDate
                },
            });
            return modifiedRequest;
        }

        // For authenticated requests with both token and user code
        if (token && userCode) {
            const modifiedRequest = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`,
                    'user-code': userCode
                },
            });
            return modifiedRequest;
        }

        // For requests with only token (free search scenarios)
        if (token) {
            const modifiedRequest = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                },
            });
            return modifiedRequest;
        }

        // For requests without any authentication
        // Don't set Content-Type for FormData requests (let browser set it automatically)
        if (request.body instanceof FormData) {
            return request;
        }
        
        const modifiedRequest = request.clone({
            setHeaders: {
                'Content-Type': 'application/json'
            },
        });
        return modifiedRequest;
    }
}
